import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { GelanggangDetail, UserProfile, NoticeItem, GelanggangClassItem } from '../../types';
import { useToast } from './Toast';
import { saveGelanggangDB } from '../../services/db';
import { processUploadedQrToSvg } from '../../utils/qrProcessor';
import { getGoogleMapsUrl, getGelanggangDisplayAddress, formatPublicName, isUserAbove18, canEditGelanggangSessionAndPrice } from '../../utils/formatters';
import { parseGelanggangClasses, getYearlyPrice, isEligibleForMonthlyFee } from '../../utils/pricing';
import { parseStartAndEndTime } from '../../utils/activityScheduler';
import { BengkungCulaBadge } from './BengkungCulaBadge';
import {
  ArrowLeft,
  Edit2,
  Calendar,
  Clock,
  MapPin,
  Navigation,
  Phone,
  User,
  Users,
  Save,
  Plus,
  Trash2,
  CheckCircle2,
  DollarSign,
  Target,
  HandFist,
  AlertCircle,
  ExternalLink,
  Info,
  Sparkles,
  Settings,
  ChevronDown,
  ChevronUp,
  Search,
  MessageCircle,
  ShieldAlert,
  Loader2,
  Sun,
  Moon,
  Timer,
  QrCode,
  CreditCard,
  Upload,
  Image as ImageIcon,
  FileText,
  Check,
  Copy,
  Building2,
  Wallet
} from 'lucide-react';

interface GelanggangSessionManageViewProps {
  gelanggang: GelanggangDetail;
  onBack: () => void;
  onSaved: (updatedGel: GelanggangDetail) => void;
  onOpenCalendar?: (g: GelanggangDetail) => void;
  membersList?: UserProfile[];
  notices?: NoticeItem[];
  gelanggangList?: GelanggangDetail[];
  onSelectGelanggang?: (g: GelanggangDetail) => void;
}

const DAYS_OF_WEEK = [
  { key: 'ahad', label: 'Ahad', full: 'Ahad' },
  { key: 'isnin', label: 'Isnin', full: 'Isnin' },
  { key: 'selasa', label: 'Selasa', full: 'Selasa' },
  { key: 'rabu', label: 'Rabu', full: 'Rabu' },
  { key: 'khamis', label: 'Khamis', full: 'Khamis' },
  { key: 'jumaat', label: 'Jumaat', full: 'Jumaat' },
  { key: 'sabtu', label: 'Sabtu', full: 'Sabtu' },
];

function time24To12(time24: string): string {
  if (!time24) return '';
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10) || 0;
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12 === 0 ? 12 : h % 12;
  const mFormatted = m < 10 ? `0${m}` : `${m}`;
  return `${h}.${mFormatted} ${period}`;
}

function time12To24(time12: string): string {
  if (!time12) return '20:30';
  const match = time12.match(/(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM|pagi|malam|petang)?/i);
  if (!match) return '20:30';
  let h = parseInt(match[1], 10);
  const m = match[2] ? parseInt(match[2], 10) : 0;
  const period = match[3] ? match[3].toUpperCase() : '';
  if ((period === 'PM' || /malam|petang/i.test(period)) && h < 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  const hStr = h < 10 ? `0${h}` : `${h}`;
  const mStr = m < 10 ? `0${m}` : `${m}`;
  return `${hStr}:${mStr}`;
}

function calculateDuration(start24: string, end24: string): string {
  if (!start24 || !end24) return '';
  const [h1, m1] = start24.split(':').map(Number);
  const [h2, m2] = end24.split(':').map(Number);
  let totalMin1 = h1 * 60 + m1;
  let totalMin2 = h2 * 60 + m2;
  if (totalMin2 < totalMin1) {
    totalMin2 += 24 * 60; // overnight session
  }
  const diff = totalMin2 - totalMin1;
  const hours = Math.floor(diff / 60);
  const mins = diff % 60;
  if (hours === 0 && mins === 0) return '0 Jam';
  if (mins === 0) return `${hours} Jam`;
  return `${hours} Jam ${mins} Minit`;
}

function parseDaysFromSessionTime(str: string): string[] {
  if (!str) return ['jumaat', 'sabtu'];
  const lower = str.toLowerCase();
  const matched = DAYS_OF_WEEK.filter(d => lower.includes(d.key)).map(d => d.key);
  return matched.length > 0 ? matched : ['jumaat', 'sabtu'];
}

function parseDaysFromDayString(dayStr?: string): string[] {
  if (!dayStr) return [];
  const lower = dayStr.toLowerCase();
  return DAYS_OF_WEEK.filter(d => lower.includes(d.key)).map(d => d.key);
}

function formatDaysFromKeys(keys: string[]): string {
  if (keys.length === 0) return '';
  const ordered = DAYS_OF_WEEK.filter(d => keys.includes(d.key));
  if (ordered.length === 1) return ordered[0].label;
  if (ordered.length === 2) return `${ordered[0].label} & ${ordered[1].label}`;
  const last = ordered[ordered.length - 1].label;
  const others = ordered.slice(0, ordered.length - 1).map(d => d.label).join(', ');
  return `${others} & ${last}`;
}


function parseScheduleDetails(sessionTime?: string, classesRaw?: GelanggangClassItem[]) {
  const { startHour, startMinute, endHour, endMinute } = parseStartAndEndTime(sessionTime);
  const sH = startHour < 10 ? '0' + startHour : '' + startHour;
  const sM = startMinute < 10 ? '0' + startMinute : '' + startMinute;
  const eH = endHour < 10 ? '0' + endHour : '' + endHour;
  const eM = endMinute < 10 ? '0' + endMinute : '' + endMinute;
  const defaultTimes = { start: sH + ':' + sM, end: eH + ':' + eM };

  const dayTimes: Record<string, { start: string; end: string }> = {};
  DAYS_OF_WEEK.forEach(d => {
    dayTimes[d.key] = { ...defaultTimes };
  });

  let hasMultipleTimes = false;

  if (sessionTime && (sessionTime.includes('&') || sessionTime.includes('('))) {
    const parts = sessionTime.split(/[&,;]+/);
    parts.forEach(part => {
      const pLower = part.toLowerCase();
      DAYS_OF_WEEK.forEach(d => {
        if (pLower.includes(d.key)) {
          const parsed = parseStartAndEndTime(part);
          const pSH = parsed.startHour < 10 ? '0' + parsed.startHour : '' + parsed.startHour;
          const pSM = parsed.startMinute < 10 ? '0' + parsed.startMinute : '' + parsed.startMinute;
          const pEH = parsed.endHour < 10 ? '0' + parsed.endHour : '' + parsed.endHour;
          const pEM = parsed.endMinute < 10 ? '0' + parsed.endMinute : '' + parsed.endMinute;
          dayTimes[d.key] = { start: pSH + ':' + pSM, end: pEH + ':' + pEM };
          if (pSH + ':' + pSM !== defaultTimes.start || pEH + ':' + pEM !== defaultTimes.end) {
            hasMultipleTimes = true;
          }
        }
      });
    });
  }

  if (classesRaw && classesRaw.length > 0) {
    classesRaw.forEach(c => {
      if (c.day && c.startTime && c.endTime) {
        const dMatch = DAYS_OF_WEEK.find(d => c.day?.toLowerCase().includes(d.key));
        if (dMatch) {
          dayTimes[dMatch.key] = { start: c.startTime, end: c.endTime };
          if (c.startTime !== defaultTimes.start || c.endTime !== defaultTimes.end) {
            hasMultipleTimes = true;
          }
        }
      }
    });
  }

  return { defaultTimes, dayTimes, hasMultipleTimes };
}

export const GelanggangSessionManageView: React.FC<GelanggangSessionManageViewProps> = ({
  gelanggang,
  onBack,
  onSaved,
  onOpenCalendar,
  membersList = [],
  gelanggangList = [],
  onSelectGelanggang,
}) => {
  const { currentUser } = useAuth();
  const canEdit = canEditGelanggangSessionAndPrice(currentUser, gelanggang);
  const { showToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Gelanggang basic details state
  const [name] = useState(gelanggang.name);
  const [status, setStatus] = useState<'Aktif' | 'Tidak Aktif'>(gelanggang.status || 'Aktif');
  const [location, setLocation] = useState(gelanggang.location || '');
  const [address, setAddress] = useState(gelanggang.address || gelanggang.location || '');
  const [gpsLocation, setGpsLocation] = useState(gelanggang.gpsLocation || '');

  // Dynamic Ketua Gelanggang resolution from member roles
  const dynamicLeader = useMemo(() => {
    const assigned = membersList.find(
      m => m.role === 'Ketua Gelanggang' && (m.gelanggang === gelanggang.name || (m as any).gelanggangId === gelanggang.id)
    ) || membersList.find(
      m => m.name && gelanggang.leaderName && m.name.trim().toLowerCase() === gelanggang.leaderName.trim().toLowerCase()
    );

    if (assigned) {
      return {
        name: assigned.name,
        phone: assigned.phone || gelanggang.leaderPhone || '',
        memberId: assigned.id,
        bengkung: assigned.bengkung || ''
      };
    }

    return {
      name: gelanggang.leaderName || '',
      phone: gelanggang.leaderPhone || '',
      memberId: '',
      bengkung: ''
    };
  }, [membersList, gelanggang]);

  // Payment preference state (FPX Persatuan vs Own QR Ketua Gelanggang)
  const [paymentPreference, setPaymentPreference] = useState<'fpx' | 'qr_ketua'>(gelanggang.paymentPreference || 'fpx');
  const [qrBankName, setQrBankName] = useState<string>(gelanggang.qrBankName || 'Maybank');
  const [qrAccountName, setQrAccountName] = useState<string>(gelanggang.qrAccountName || gelanggang.leaderName || '');
  const [qrAccountNo, setQrAccountNo] = useState<string>(gelanggang.qrAccountNo || '');
  const [qrImageUrl, setQrImageUrl] = useState<string>(gelanggang.qrImageUrl || '');
  const [qrNotes, setQrNotes] = useState<string>(gelanggang.qrNotes || '');
  const [isQrUploading, setIsQrUploading] = useState<boolean>(false);

  useEffect(() => {
    if (!qrAccountName && dynamicLeader.name) {
      setQrAccountName(dynamicLeader.name);
    }
  }, [dynamicLeader.name]);

  const handleQrImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEdit) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('error', 'Fail Tidak Sah', 'Sila muat naik fail imej (PNG / JPEG / WebP) bagi kod QR.');
      return;
    }
    setIsQrUploading(true);
    try {
      // Auto-process and decode QR code into clean 1:1 SVG
      const processed = await processUploadedQrToSvg(file);
      if (processed) {
        setQrImageUrl(processed.svgDataUrl);
        setIsQrUploading(false);
        showToast('success', 'Kod QR Ditukar ke Digital SVG (1:1)', 'Imej kod QR berjaya diimbas dan dijana semula ke format 1:1 SVG digital tulen tanpa sebarang bingkai.');
        return;
      }
    } catch (err) {
      console.warn('Auto SVG QR processing error:', err);
    }

    // Fallback if jsQR cannot detect QR pattern in file
    const reader = new FileReader();
    reader.onload = () => {
      setQrImageUrl(reader.result as string);
      setIsQrUploading(false);
      showToast('info', 'Imej Dimuat Naik', 'Imej disimpan. (Nota: Tiada corak QR dikesan untuk penjanaan SVG, imej asal digunakan).');
    };
    reader.onerror = () => {
      setIsQrUploading(false);
      showToast('error', 'Ralat', 'Gagal membaca imej kod QR.');
    };
    reader.readAsDataURL(file);
  };

  // Schedule & Class helpers
  const initialScheduleParsed = useMemo(() => {
    const rawCls = parseGelanggangClasses(gelanggang.name, gelanggang.classes);
    return parseScheduleDetails(gelanggang.sessionTime, rawCls);
  }, [gelanggang.name, gelanggang.sessionTime, gelanggang.classes]);

  // Schedule builder state
  const [selectedDays, setSelectedDays] = useState<string[]>(() => parseDaysFromSessionTime(gelanggang.sessionTime));
  const [globalStartTime, setGlobalStartTime] = useState<string>(initialScheduleParsed.defaultTimes.start);
  const [globalEndTime, setGlobalEndTime] = useState<string>(initialScheduleParsed.defaultTimes.end);
  const [isDifferentTimePerDay, setIsDifferentTimePerDay] = useState<boolean>(initialScheduleParsed.hasMultipleTimes);
  const [dayTimes, setDayTimes] = useState<Record<string, { start: string; end: string }>>(initialScheduleParsed.dayTimes);
  const [isManualScheduleMode, setIsManualScheduleMode] = useState<boolean>(false);
  const [manualSessionTime, setManualSessionTime] = useState<string>(gelanggang.sessionTime || '');

  // Classes state
  const [classesList, setClassesList] = useState<GelanggangClassItem[]>(() => {
    return parseGelanggangClasses(gelanggang.name, gelanggang.classes).map(c => {
      const clsAny = c as any;
      let sTime = clsAny.startTime;
      let eTime = clsAny.endTime;
      let dDay = clsAny.day || '';

      // If class already has schedule string, extract start/end time and day if not explicitly set
      if ((!sTime || !eTime) && clsAny.schedule) {
        const parsed = parseStartAndEndTime(clsAny.schedule);
        if (parsed) {
          const sH = parsed.startHour < 10 ? `0${parsed.startHour}` : `${parsed.startHour}`;
          const sM = parsed.startMinute < 10 ? `0${parsed.startMinute}` : `${parsed.startMinute}`;
          const eH = parsed.endHour < 10 ? `0${parsed.endHour}` : `${parsed.endHour}`;
          const eM = parsed.endMinute < 10 ? `0${parsed.endMinute}` : `${parsed.endMinute}`;
          sTime = `${sH}:${sM}`;
          eTime = `${eH}:${eM}`;
        }
      }
      if (!dDay && clsAny.schedule) {
        const dayMatch = DAYS_OF_WEEK.filter(d => clsAny.schedule.toLowerCase().includes(d.key)).map(d => d.label);
        if (dayMatch.length === 1) dDay = dayMatch[0];
        else if (dayMatch.length === 2) dDay = `${dayMatch[0]} & ${dayMatch[1]}`;
      }

      // Sensible defaults for Memanah (traditional archery usually runs Sunday mornings)
      if (!sTime && c.name.toLowerCase().includes('memanah')) {
        sTime = '08:00';
      }
      if (!eTime && c.name.toLowerCase().includes('memanah')) {
        eTime = '10:00';
      }
      if (!dDay && c.name.toLowerCase().includes('memanah')) {
        dDay = 'Ahad';
      }

      const finalSTime = sTime || initialScheduleParsed.defaultTimes.start;
      const finalETime = eTime || initialScheduleParsed.defaultTimes.end;
      const cleanDay = (dDay && dDay !== 'Semua Hari Sesi' && dDay !== 'Ikut Sesi Gelanggang') ? dDay : '';
      const timeRange = `${time24To12(finalSTime)} - ${time24To12(finalETime)}`;
      const formattedSched = cleanDay ? `${cleanDay} (${timeRange})` : timeRange;

      return {
        name: c.name,
        price: c.price,
        day: cleanDay || undefined,
        startTime: finalSTime,
        endTime: finalETime,
        schedule: clsAny.schedule || formattedSched,
        category: clsAny.category || (c.name.toLowerCase().includes('dewasa') ? 'Dewasa' : c.name.toLowerCase().includes('muda') ? 'Kanak-kanak' : 'Terbuka'),
        notes: clsAny.notes || ''
      };
    });
  });

  const handleApplyPusakaPreset = () => {
    setSelectedDays(['jumaat', 'sabtu']);
    setIsDifferentTimePerDay(true);
    setDayTimes(prev => ({
      ...prev,
      jumaat: { start: '08:00', end: '11:00' },
      sabtu: { start: '20:00', end: '22:00' }
    }));
    setClassesList([
      {
        name: 'Latihan Silat (Muda & Dewasa)',
        price: 20,
        category: 'Terbuka',
        day: 'Jumaat',
        startTime: '08:00',
        endTime: '11:00',
        schedule: 'Jumaat (8.00 AM - 11.00 AM)',
        notes: 'Sesi pagi bersama pesilat muda dan dewasa'
      },
      {
        name: 'Latihan Silat (Dewasa)',
        price: 30,
        category: 'Dewasa',
        day: 'Sabtu',
        startTime: '20:00',
        endTime: '22:00',
        schedule: 'Sabtu (8.00 PM - 10.00 PM)',
        notes: 'Sesi malam kependekaran pesilat dewasa'
      }
    ]);
    setIsManualScheduleMode(false);
    showToast('success', 'Tetapan Pusaka Warisan Diterapkan', 'Jadual Jumaat (8.00 AM - 11.00 AM) & Sabtu (8.00 PM - 10.00 PM) berserta kelas Latihan Silat (Muda & Dewasa) dan Latihan Silat (Dewasa) telah dikemaskini.');
  };

  const handleApplyWadiPreset = () => {
    setSelectedDays(['jumaat']);
    setIsDifferentTimePerDay(false);
    setGlobalStartTime('15:30');
    setGlobalEndTime('18:30');
    setClassesList([
      {
        name: 'Latihan Memanah',
        price: 20,
        category: 'Terbuka',
        day: 'Jumaat',
        startTime: '15:30',
        endTime: '17:00',
        schedule: 'Jumaat (3.30 PM - 5.00 PM)',
        notes: 'Sesi memanah tradisional petang Jumaat'
      },
      {
        name: 'Latihan Silat',
        price: 20,
        category: 'Terbuka',
        day: 'Jumaat',
        startTime: '17:00',
        endTime: '18:30',
        schedule: 'Jumaat (5.00 PM - 6.30 PM)',
        notes: 'Sesi silat tempur dan seni petang Jumaat'
      }
    ]);
    setIsManualScheduleMode(false);
    showToast('success', 'Tetapan Wadi AnNour Diterapkan', 'Jadual Jumaat (3.30 PM - 6.30 PM) berserta kelas Latihan Memanah dan Latihan Silat telah dikemaskini.');
  };

  // Helper to update a class item immutably with auto-calculated schedule string
  const updateClassItem = (index: number, updates: Partial<GelanggangClassItem>) => {
    setClassesList(prev => {
      const next = [...prev];
      const current = next[index];
      const sTime = updates.startTime !== undefined ? updates.startTime : (current.startTime || globalStartTime);
      const eTime = updates.endTime !== undefined ? updates.endTime : (current.endTime || globalEndTime);
      const dayVal = updates.day !== undefined ? updates.day : current.day;
      const cleanDay = (dayVal && dayVal !== 'Semua Hari Sesi' && dayVal !== 'Ikut Sesi Gelanggang') ? dayVal : '';
      const timeRange = `${time24To12(sTime)} - ${time24To12(eTime)}`;
      const schedule = cleanDay ? `${cleanDay} (${timeRange})` : timeRange;

      next[index] = {
        ...current,
        ...updates,
        startTime: sTime,
        endTime: eTime,
        day: cleanDay || undefined,
        schedule
      };
      return next;
    });
  };

  // Add new class form state
  const [isAddingClass, setIsAddingClass] = useState<boolean>(false);
  const [newClassName, setNewClassName] = useState<string>('Silat');
  const [customClassName, setCustomClassName] = useState<string>('');
  const [newClassPrice, setNewClassPrice] = useState<number>(40);
  const [newClassDay, setNewClassDay] = useState<string>('Semua Hari Sesi');
  const [newClassStartTime, setNewClassStartTime] = useState<string>('20:30');
  const [newClassEndTime, setNewClassEndTime] = useState<string>('22:30');
  const [newClassCategory, setNewClassCategory] = useState<'Dewasa' | 'Kanak-kanak' | 'Terbuka'>('Terbuka');

  // Filter registered pesilat for this gelanggang
  const enrolledMembers = useMemo(() => {
    return membersList.filter(m => {
      if (m.status && !['Aktif', 'Menunggu Pengesahan'].includes(m.status)) return false;
      if (m.gelanggang !== gelanggang.name) return false;
      if (m.role === 'Waris') return false;
      if (m.role === 'Ketua Gelanggang') return false;
      if (dynamicLeader.name && m.name && (
        m.name.trim().toLowerCase() === dynamicLeader.name.trim().toLowerCase() ||
        m.name.toLowerCase().includes(dynamicLeader.name.toLowerCase()) ||
        dynamicLeader.name.toLowerCase().includes(m.name.toLowerCase())
      )) return false;
      return true;
    });
  }, [membersList, gelanggang.name, dynamicLeader.name]);

  // Dynamic schedule string generation derived from configured classes
  const generatedScheduleString = useMemo(() => {
    if (isManualScheduleMode) {
      return manualSessionTime;
    }
    if (!classesList || classesList.length === 0) {
      return gelanggang.sessionTime || 'Tiada Sesi';
    }

    const dayMap = new Map<string, { start: string; end: string; dayLabel: string; dayIndex: number }>();

    classesList.forEach(cls => {
      const dayStr = cls.day || 'Jumaat';
      const dayObj = DAYS_OF_WEEK.find(d => dayStr.toLowerCase().includes(d.key)) || DAYS_OF_WEEK[5];
      const s = cls.startTime || '20:30';
      const e = cls.endTime || '22:30';

      if (!dayMap.has(dayObj.key)) {
        dayMap.set(dayObj.key, {
          start: s,
          end: e,
          dayLabel: dayObj.label,
          dayIndex: DAYS_OF_WEEK.findIndex(d => d.key === dayObj.key)
        });
      } else {
        const existing = dayMap.get(dayObj.key)!;
        const [h1, m1] = existing.start.split(':').map(Number);
        const [h2, m2] = s.split(':').map(Number);
        const [eh1, em1] = existing.end.split(':').map(Number);
        const [eh2, em2] = e.split(':').map(Number);

        const earlierStart = (h2 * 60 + m2) < (h1 * 60 + m1) ? s : existing.start;
        const laterEnd = (eh2 * 60 + em2) > (eh1 * 60 + em1) ? e : existing.end;
        existing.start = earlierStart;
        existing.end = laterEnd;
      }
    });

    const sorted = Array.from(dayMap.values()).sort((a, b) => a.dayIndex - b.dayIndex);
    if (sorted.length === 0) return gelanggang.sessionTime || 'Tiada Sesi';

    return sorted.map(d => {
      const range = `${time24To12(d.start)} - ${time24To12(d.end)}`;
      return `${d.dayLabel} (${range})`;
    }).join(' & ');
  }, [classesList, isManualScheduleMode, manualSessionTime, gelanggang.sessionTime]);

  // Projected monthly & yearly revenue
  const financialSummary = useMemo(() => {
    let monthlyTotal = 0;
    enrolledMembers.forEach(mem => {
      if (isEligibleForMonthlyFee(mem)) {
        const isDewasa = mem.relationship !== 'Anak' && (mem.icNo ? isUserAbove18(mem.icNo) : true);
        const matched = classesList.find(c => {
          if (c.name.toLowerCase().includes('dewasa') && isDewasa) return true;
          if (c.name.toLowerCase().includes('muda') && !isDewasa) return true;
          return c.name.toLowerCase().includes('silat');
        }) || classesList[0];
        if (matched) {
          monthlyTotal += matched.price;
        }
      }
    });
    const yearlyTotal = getYearlyPrice(monthlyTotal);
    return { monthlyTotal, yearlyTotal };
  }, [enrolledMembers, classesList]);

  // Day toggle handler
  const handleToggleDay = (dayKey: string) => {
    if (!canEdit) return;
    if (selectedDays.includes(dayKey)) {
      setSelectedDays(selectedDays.filter(k => k !== dayKey));
    } else {
      setSelectedDays([...selectedDays, dayKey]);
    }
  };

  // Add class handler
  const handleAddClass = () => {
    if (!canEdit) return;
    const finalName = newClassName === 'Lain-lain' ? customClassName.trim() : newClassName;
    if (!finalName) {
      showToast('error', 'Nama Kelas Diperlukan', 'Sila masukkan nama aktiviti atau kelas latihan.');
      return;
    }
    if (classesList.some(c => c.name.toLowerCase() === finalName.toLowerCase())) {
      showToast('error', 'Kelas Telah Wujud', `Kelas ${finalName} sudah berada dalam senarai.`);
      return;
    }

    const timeRange = `${time24To12(newClassStartTime)} - ${time24To12(newClassEndTime)}`;
    const scheduleStr = newClassDay && newClassDay !== 'Semua Hari Sesi'
      ? `${newClassDay} (${timeRange})`
      : timeRange;

    const newClass: GelanggangClassItem = {
      name: finalName,
      price: Math.max(0, newClassPrice),
      day: newClassDay !== 'Semua Hari Sesi' ? newClassDay : undefined,
      startTime: newClassStartTime,
      endTime: newClassEndTime,
      schedule: scheduleStr,
      category: newClassCategory,
    };

    setClassesList([...classesList, newClass]);
    if (newClassName === 'Lain-lain') setCustomClassName('');
    showToast('success', 'Kelas Ditambah', `Kelas ${finalName} telah ditambah dengan waktu ${timeRange}.`);
  };

  // Remove class handler
  const handleRemoveClass = (index: number) => {
    if (!canEdit) return;
    const target = classesList[index];
    setClassesList(classesList.filter((_, idx) => idx !== index));
    showToast('info', 'Kelas Dikeluarkan', `Kelas ${target.name} telah dikeluarkan.`);
  };

  // List of classes that have custom start/end time or custom training day
  const classesWithCustomTimes = useMemo(() => {
    return classesList.filter(c => {
      const isDiffStart = c.startTime && c.startTime !== globalStartTime;
      const isDiffEnd = c.endTime && c.endTime !== globalEndTime;
      const isDiffDay = !!c.day && c.day !== 'Semua Hari Sesi' && c.day !== 'Ikut Sesi Gelanggang';
      return isDiffStart || isDiffEnd || isDiffDay;
    });
  }, [classesList, globalStartTime, globalEndTime]);

  // Save all changes
  const handleSaveAll = async () => {
    if (!canEdit) {
      showToast('error', 'Akses Terhad', 'Hanya Ketua Gelanggang bagi gelanggang ini atau Superadmin dibenarkan menyimpan perubahan.');
      return;
    }
    setIsSaving(true);
    try {
      const finalSessionTime = isManualScheduleMode ? manualSessionTime.trim() : generatedScheduleString.trim();

      // Ensure each class has its updated schedule string and times
      const finalizedClasses = classesList.map(c => {
        const sTime = c.startTime || globalStartTime;
        const eTime = c.endTime || globalEndTime;
        const cleanDay = (c.day && c.day !== 'Semua Hari Sesi' && c.day !== 'Ikut Sesi Gelanggang') ? c.day : '';
        const timeRange = `${time24To12(sTime)} - ${time24To12(eTime)}`;
        const sched = cleanDay ? `${cleanDay} (${timeRange})` : timeRange;
        return {
          ...c,
          startTime: sTime,
          endTime: eTime,
          day: cleanDay || undefined,
          schedule: sched
        };
      });

      const updatedGelanggang: GelanggangDetail = {
        ...gelanggang,
        name: gelanggang.name,
        status,
        sessionTime: finalSessionTime || gelanggang.sessionTime,
        location: location.trim() || gelanggang.location,
        address: address.trim() || gelanggang.address,
        gpsLocation: gpsLocation.trim(),
        leaderName: dynamicLeader.name || gelanggang.leaderName,
        leaderPhone: dynamicLeader.phone || gelanggang.leaderPhone,
        classes: JSON.stringify(finalizedClasses),
        paymentPreference,
        qrBankName: qrBankName.trim(),
        qrAccountName: qrAccountName.trim(),
        qrAccountNo: qrAccountNo.trim(),
        qrImageUrl,
        qrNotes: qrNotes.trim()
      };

      await saveGelanggangDB(updatedGelanggang);
      showToast('success', 'Perubahan Disimpan', `Jadual sesi latihan, waktu mula/tamat, dan harga gelanggang ${gelanggang.name} berjaya dikemaskini.`);
      onSaved(updatedGelanggang);
    } catch (err) {
      showToast('error', 'Gagal Menyimpan', 'Berlaku ralat semasa menyimpan maklumat. Sila cuba lagi.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-16 animate-fade-in">
      {/* Top Header & Sticky Action Bar */}
      <div className="bg-white p-4 sm:p-6 rounded-3xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-2.5 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border-none cursor-pointer shrink-0 mt-0.5"
            title="Kembali ke Senarai Gelanggang"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-red-50 text-asmm-red text-[11px] font-extrabold rounded-full border border-red-100">
                <Settings className="w-3.5 h-3.5" />
                <span>Pengurusan Sesi & Harga</span>
              </span>
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-extrabold rounded-full border ${status === 'Aktif'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-slate-100 text-slate-500 border-slate-200'
                }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${status === 'Aktif' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                <span>{status}</span>
              </span>
            </div>
                        <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                Gelanggang {gelanggang.name}
              </h1>
              {gelanggangList && gelanggangList.length > 1 && onSelectGelanggang && (
                <div className="inline-flex items-center gap-1 p-1 bg-slate-100 rounded-2xl border border-slate-200">
                  {gelanggangList.filter(g => g.status === 'Aktif').map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => onSelectGelanggang(g)}
                      className={`px-3 py-1 rounded-xl text-xs font-black transition-all cursor-pointer border-none ${
                        g.id === gelanggang.id
                          ? 'bg-slate-900 text-amber-400 shadow-xs'
                          : 'bg-transparent text-slate-600 hover:text-slate-900 hover:bg-white/60'
                      }`}
                    >
                      {g.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="truncate max-w-md">{address || location || 'Kedah Darul Aman'}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 self-end md:self-center">
          {onOpenCalendar && (
            <button
              type="button"
              onClick={() => onOpenCalendar(gelanggang)}
              className="px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-2xl transition-all border-none cursor-pointer flex items-center gap-2"
            >
              <Calendar className="w-4 h-4 text-slate-600" />
              <span className="hidden sm:inline">Kalendar Latihan</span>
            </button>
          )}

          {canEdit ? (
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={isSaving}
              className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson disabled:opacity-50 text-white text-xs font-black rounded-2xl transition-all shadow-md hover:shadow-lg flex items-center gap-2 cursor-pointer border-none"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Menyimpan...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Simpan Perubahan</span>
                </>
              )}
            </button>
          ) : (
            <div className="px-3.5 py-2 bg-slate-100 text-slate-600 text-xs font-bold rounded-2xl flex items-center gap-1.5 border border-slate-200">
              <ShieldAlert className="w-4 h-4 text-slate-500" />
              <span>Mod Paparan Sahaja</span>
            </div>
          )}
        </div>
      </div>

      {/* Read-Only Warning Banner */}
      {!canEdit && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 p-4 sm:p-5 rounded-3xl flex items-start gap-3.5 text-xs shadow-xs">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="font-extrabold text-amber-950 text-sm flex items-center gap-2">
              <span>Mod Paparan Sahaja (Akses Terhad)</span>
              <span className="text-[10px] bg-amber-200/80 text-amber-900 font-black px-2 py-0.5 rounded-full">Hanya Ketua Gelanggang / Superadmin</span>
            </h4>
            <p className="text-amber-800 leading-relaxed text-xs">
              Hanya Ketua Gelanggang bagi <strong>Gelanggang {gelanggang.name}</strong> ({dynamicLeader.name || gelanggang.leaderName || 'Ketua Gelanggang'}) atau <strong>Superadmin</strong> yang dibenarkan mengubah jadual sesi latihan, masa, senarai kelas, saluran bayaran, dan harga yuran gelanggang ini.
            </p>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Columns: Schedule Builder & Classes Management */}
        <div className="lg:col-span-2 space-y-6">

          {/* Unified Card: Jadual Sesi Latihan & Penetapan Yuran (1 Tempat Sahaja) */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200 shadow-2xs space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <span className="p-2.5 rounded-2xl bg-red-50 text-asmm-red border border-red-100">
                  <HandFist className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-sm font-black text-slate-900">Jadual Sesi Latihan & Penetapan Yuran</h2>
                  <p className="text-[11px] text-slate-500">Tetapkan hari latihan, masa mula/tamat, dan yuran bagi setiap sesi di bawah. Format rasmi gelanggang dijana secara automatik.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 self-start sm:self-center">
                <span className="text-xs font-black text-asmm-red bg-red-50 px-2.5 py-1 rounded-xl border border-red-100">
                  {classesList.length} Sesi Aktif
                </span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!isManualScheduleMode) {
                        setManualSessionTime(generatedScheduleString);
                      }
                      setIsManualScheduleMode(!isManualScheduleMode);
                    }}
                    className="text-[11px] font-bold text-slate-500 hover:text-slate-800 underline bg-transparent border-none cursor-pointer"
                  >
                    {isManualScheduleMode ? 'Gunakan Auto-Format' : 'Mod Teks Manual'}
                  </button>
                )}
              </div>
            </div>

            {/* Live Generated Schedule Summary Banner */}
            {!isManualScheduleMode ? (
              <div className="p-3.5 bg-slate-50/90 border border-slate-200/80 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="p-1.5 rounded-xl bg-red-100 text-red-600 shrink-0">
                    <Calendar className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Jadual Rasmi Gelanggang Dijana
                    </div>
                    <div className="text-xs sm:text-sm font-extrabold text-slate-900 truncate">
                      {generatedScheduleString || '(Sila tetapkan sekurang-kurangnya satu sesi di bawah)'}
                    </div>
                  </div>
                </div>
                <span className="text-[10px] font-bold text-slate-500 bg-white px-2.5 py-1 rounded-full border border-slate-200/80 shrink-0 self-start sm:self-auto shadow-3xs">
                  Auto-sync Kalendar
                </span>
              </div>
            ) : (
              <div className="p-4 bg-amber-50/70 border border-amber-200/80 rounded-2xl space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black text-amber-900 flex items-center gap-1.5">
                    <Edit2 className="w-3.5 h-3.5 text-amber-700" />
                    <span>Teks Manual Jadual Gelanggang</span>
                  </span>
                  <span className="text-[10px] font-extrabold text-amber-800 bg-amber-200 px-2 py-0.5 rounded">Mod Manual Aktif</span>
                </div>
                <input
                  type="text"
                  value={manualSessionTime}
                  disabled={!canEdit}
                  onChange={(e) => setManualSessionTime(e.target.value)}
                  placeholder="cth: Jumaat (8.00 AM - 11.00 AM) & Sabtu (8.00 PM - 10.00 PM)"
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                />
              </div>
            )}

            {/* List of Sesi / Aktiviti Latihan */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">
                    Senarai Sesi & Kelas Latihan ({classesList.length})
                  </h3>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Tetapkan hari latihan, masa, dan yuran bulanan untuk setiap sesi.
                  </p>
                </div>
                {canEdit && !isAddingClass && (
                  <button
                    type="button"
                    onClick={() => setIsAddingClass(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer border-none"
                  >
                    <Plus className="w-3.5 h-3.5 text-amber-400" />
                    <span>Tambah Sesi</span>
                  </button>
                )}
              </div>

              <div className="space-y-3.5">
                {classesList.map((cls, idx) => {
                  const isMemanah = cls.name.toLowerCase().includes('memanah');
                  const yearlyPrice = getYearlyPrice(cls.price);
                  const classStart = cls.startTime || '20:30';
                  const classEnd = cls.endTime || '22:30';
                  const duration = calculateDuration(classStart, classEnd);
                  const classDaysList = parseDaysFromDayString(cls.day);

                  const enrolledInClass = enrolledMembers.filter(m => {
                    if (cls.name.toLowerCase().includes('memanah')) {
                      return m.sessionTime?.toLowerCase().includes('memanah');
                    }
                    if (cls.name.toLowerCase().includes('dewasa')) {
                      return m.relationship !== 'Anak' && (m.icNo ? isUserAbove18(m.icNo) : true);
                    }
                    if (cls.name.toLowerCase().includes('muda')) {
                      return m.relationship === 'Anak' || (m.icNo ? !isUserAbove18(m.icNo) : false);
                    }
                    return true;
                  }).length;

                  return (
                    <div
                      key={idx}
                      className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs hover:border-slate-300 transition-all space-y-4"
                    >
                      {/* Top Header: Icon + Name + Category + Duration + Pricing + Delete */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3.5 border-b border-slate-100">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div
                            className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border ${
                              isMemanah
                                ? 'bg-amber-50 border-amber-200 text-amber-700'
                                : 'bg-red-50 border-red-100 text-red-600'
                            }`}
                          >
                            {isMemanah ? <Target className="w-5 h-5" /> : <HandFist className="w-5 h-5" />}
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-sm sm:text-base font-extrabold text-slate-900 tracking-tight">{cls.name}</h4>
                              <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 shrink-0">
                                {cls.category || 'Terbuka'}
                              </span>
                              <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
                                {duration}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                              {enrolledInClass} pesilat berdaftar
                            </p>
                          </div>
                        </div>

                        {/* Price Input & Delete Button - Perfectly aligned horizontal row, no wrapping */}
                        <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                          <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-2.5 h-9 focus-within:bg-white focus-within:border-red-600 transition-all">
                            <span className="text-xs font-bold text-slate-400 mr-1">RM</span>
                            <input
                              type="number"
                              min="0"
                              value={cls.price}
                              disabled={!canEdit}
                              onChange={(e) => {
                                const val = Math.max(0, parseInt(e.target.value, 10) || 0);
                                updateClassItem(idx, { price: val });
                              }}
                              className="w-12 bg-transparent border-none focus:outline-none text-xs font-extrabold text-slate-900 p-0 disabled:opacity-75"
                            />
                            <span className="text-[10px] font-bold text-slate-400">/bln</span>
                          </div>

                          <span className="inline-flex items-center text-[10px] font-bold text-amber-800 bg-amber-50 px-2.5 h-9 rounded-xl border border-amber-200 shrink-0 whitespace-nowrap">
                            RM{yearlyPrice}/thn (-10%)
                          </span>

                          {canEdit && classesList.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveClass(idx)}
                              className="w-9 h-9 rounded-xl border border-slate-200 hover:border-red-200 text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all flex items-center justify-center shrink-0 cursor-pointer bg-white shadow-3xs"
                              title="Padam Sesi Ini"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Day & Time Settings - Spacious & Pixel-Aligned 2-Column Responsive Layout */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Hari Latihan */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between h-5">
                            <label className="text-xs font-bold text-slate-700">Hari Sesi Latihan</label>
                            <span className="text-[11px] font-bold text-red-600">{cls.day || 'Pilih Hari'}</span>
                          </div>
                          <div className="grid grid-cols-7 gap-1">
                            {[
                              { key: 'ahad', label: 'Aha', full: 'Ahad' },
                              { key: 'isnin', label: 'Isn', full: 'Isnin' },
                              { key: 'selasa', label: 'Sel', full: 'Selasa' },
                              { key: 'rabu', label: 'Rab', full: 'Rabu' },
                              { key: 'khamis', label: 'Kha', full: 'Khamis' },
                              { key: 'jumaat', label: 'Jum', full: 'Jumaat' },
                              { key: 'sabtu', label: 'Sab', full: 'Sabtu' },
                            ].map(d => {
                              const isDaySelected = classDaysList.includes(d.key);
                              return (
                                <button
                                  key={d.key}
                                  type="button"
                                  disabled={!canEdit}
                                  title={d.full}
                                  onClick={() => {
                                    let newKeys;
                                    if (isDaySelected) {
                                      newKeys = classDaysList.filter(k => k !== d.key);
                                    } else {
                                      newKeys = [...classDaysList, d.key];
                                    }
                                    const formatted = formatDaysFromKeys(newKeys);
                                    updateClassItem(idx, { day: formatted || undefined });
                                  }}
                                  className={`h-9 text-xs font-bold rounded-xl border flex items-center justify-center transition-all cursor-pointer ${
                                    isDaySelected
                                      ? 'bg-slate-900 text-amber-400 border-slate-900 shadow-xs'
                                      : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200/80'
                                  }`}
                                >
                                  {d.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Masa Sesi Latihan */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between h-5">
                            <label className="text-xs font-bold text-slate-700">Masa Sesi Latihan</label>
                            <span className="text-[11px] font-bold text-slate-500 font-mono">
                              {time24To12(classStart)} - {time24To12(classEnd)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <div className="flex items-center justify-center bg-slate-50 border border-slate-200 rounded-xl px-2.5 h-9 focus-within:bg-white focus-within:border-red-600 transition-all">
                                <input
                                  type="time"
                                  value={classStart}
                                  disabled={!canEdit}
                                  onChange={(e) => updateClassItem(idx, { startTime: e.target.value })}
                                  className="w-full bg-transparent border-none focus:outline-none text-xs font-bold text-slate-900 p-0 text-center"
                                />
                              </div>
                              <span className="block text-[10px] font-medium text-slate-400 mt-1 pl-1 text-left">Masa Mula</span>
                            </div>

                            <div>
                              <div className="flex items-center justify-center bg-slate-50 border border-slate-200 rounded-xl px-2.5 h-9 focus-within:bg-white focus-within:border-red-600 transition-all">
                                <input
                                  type="time"
                                  value={classEnd}
                                  disabled={!canEdit}
                                  onChange={(e) => updateClassItem(idx, { endTime: e.target.value })}
                                  className="w-full bg-transparent border-none focus:outline-none text-xs font-bold text-slate-900 p-0 text-center"
                                />
                              </div>
                              <span className="block text-[10px] font-medium text-slate-400 mt-1 pl-1 text-left">Masa Tamat</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Borang Tambah Sesi / Aktiviti Baharu (Collapsible & Clean) */}
            {canEdit && (
              isAddingClass ? (
                <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-xs space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                    <span className="text-xs font-extrabold text-slate-900 flex items-center gap-1.5">
                      <Plus className="w-4 h-4 text-red-600" />
                      <span>Tambah Sesi Aktiviti Baharu</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsAddingClass(false)}
                      className="text-xs font-bold text-slate-400 hover:text-slate-700 cursor-pointer bg-transparent border-none"
                    >
                      Batal
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 mb-1">Nama Aktiviti *</label>
                      <select
                        value={newClassName}
                        onChange={(e) => {
                          const val = e.target.value;
                          setNewClassName(val);
                          if (val === 'Latihan Silat (Muda & Dewasa)') {
                            setNewClassPrice(20);
                            setNewClassCategory('Terbuka');
                            setNewClassDay('Jumaat');
                            setNewClassStartTime('08:00');
                            setNewClassEndTime('11:00');
                          } else if (val === 'Latihan Silat (Dewasa)') {
                            setNewClassPrice(30);
                            setNewClassCategory('Dewasa');
                            setNewClassDay('Sabtu');
                            setNewClassStartTime('20:00');
                            setNewClassEndTime('22:00');
                          } else if (val === 'Latihan Memanah') {
                            setNewClassPrice(20);
                            setNewClassCategory('Terbuka');
                            setNewClassDay('Jumaat');
                            setNewClassStartTime('15:30');
                            setNewClassEndTime('17:00');
                          } else if (val === 'Latihan Silat') {
                            setNewClassPrice(20);
                            setNewClassCategory('Terbuka');
                            setNewClassDay('Jumaat');
                            setNewClassStartTime('17:00');
                            setNewClassEndTime('18:30');
                          } else if (val === 'Silat Dewasa') {
                            setNewClassPrice(50);
                            setNewClassCategory('Dewasa');
                            setNewClassDay('Sabtu');
                            setNewClassStartTime('20:00');
                            setNewClassEndTime('22:00');
                          } else if (val === 'Silat Muda') {
                            setNewClassPrice(20);
                            setNewClassCategory('Kanak-kanak');
                            setNewClassDay('Jumaat');
                            setNewClassStartTime('08:00');
                            setNewClassEndTime('11:00');
                          } else if (val === 'Silat') {
                            setNewClassPrice(40);
                            setNewClassCategory('Terbuka');
                            setNewClassDay('Jumaat');
                            setNewClassStartTime('20:00');
                            setNewClassEndTime('22:00');
                          } else if (val === 'Memanah') {
                            setNewClassPrice(20);
                            setNewClassCategory('Terbuka');
                            setNewClassDay('Jumaat');
                            setNewClassStartTime('15:30');
                            setNewClassEndTime('17:00');
                          } else if (val === 'Seni Tari & Senjata') {
                            setNewClassPrice(45);
                            setNewClassCategory('Terbuka');
                            setNewClassDay('Ahad');
                            setNewClassStartTime('10:30');
                            setNewClassEndTime('12:30');
                          }
                        }}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-red-600"
                      >
                        <option value="Latihan Silat (Muda & Dewasa)">Latihan Silat (Muda & Dewasa) - Jumaat Pagi</option>
                        <option value="Latihan Silat (Dewasa)">Latihan Silat (Dewasa) - Sabtu Malam</option>
                        <option value="Latihan Memanah">Latihan Memanah - Jumaat Petang</option>
                        <option value="Latihan Silat">Latihan Silat - Jumaat Petang</option>
                        <option value="Silat">Silat</option>
                        <option value="Silat Dewasa">Silat Dewasa</option>
                        <option value="Silat Muda">Silat Muda</option>
                        <option value="Memanah">Memanah</option>
                        <option value="Seni Tari & Senjata">Seni Tari & Senjata</option>
                        <option value="Lain-lain">Lain-lain (Tulis Sendiri)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 mb-1">Yuran Bulanan (RM) *</label>
                      <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 focus-within:bg-white focus-within:border-red-600">
                        <span className="text-xs font-extrabold text-slate-400 mr-1.5">RM</span>
                        <input
                          type="number"
                          min="0"
                          value={newClassPrice}
                          onChange={(e) => setNewClassPrice(Math.max(0, parseInt(e.target.value, 10) || 0))}
                          className="w-full bg-transparent border-none focus:outline-none text-xs font-extrabold text-slate-900 p-0"
                        />
                        <span className="text-[10px] text-slate-400 font-bold">/bln</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 mb-1">Kategori Sasaran</label>
                      <select
                        value={newClassCategory}
                        onChange={(e) => setNewClassCategory(e.target.value as any)}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-red-600"
                      >
                        <option value="Terbuka">Terbuka</option>
                        <option value="Dewasa">Dewasa (18+)</option>
                        <option value="Kanak-kanak">Kanak-kanak / Remaja (&lt;18)</option>
                      </select>
                    </div>

                    {newClassName === 'Lain-lain' && (
                      <div className="sm:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Nama Kelas Kustom *</label>
                        <input
                          type="text"
                          value={customClassName}
                          onChange={(e) => setCustomClassName(e.target.value)}
                          placeholder="Masukkan nama aktiviti (contoh: Silat Tempur)"
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-red-600"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 mb-1">Hari Sesi Kelas</label>
                      <select
                        value={newClassDay}
                        onChange={(e) => setNewClassDay(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-red-600"
                      >
                        {DAYS_OF_WEEK.map(d => (
                          <option key={d.key} value={d.label}>{d.label}</option>
                        ))}
                        <option value="Jumaat & Sabtu">Jumaat & Sabtu</option>
                        <option value="Sabtu & Ahad">Sabtu & Ahad</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 mb-1">Masa Mula</label>
                      <input
                        type="time"
                        value={newClassStartTime}
                        onChange={(e) => setNewClassStartTime(e.target.value)}
                        className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-extrabold text-slate-900 focus:outline-none focus:border-red-600"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 mb-1">Masa Tamat</label>
                      <input
                        type="time"
                        value={newClassEndTime}
                        onChange={(e) => setNewClassEndTime(e.target.value)}
                        className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-extrabold text-slate-900 focus:outline-none focus:border-red-600"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setIsAddingClass(false)}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer border-none"
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleAddClass();
                        setIsAddingClass(false);
                      }}
                      className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer border-none"
                    >
                      <Plus className="w-4 h-4 text-amber-400" />
                      <span>Simpan Sesi Aktiviti</span>
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsAddingClass(true)}
                  className="w-full py-3.5 bg-slate-50 hover:bg-slate-100 text-slate-700 hover:text-slate-900 text-xs font-bold rounded-2xl border-2 border-dashed border-slate-200 hover:border-slate-300 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-3xs"
                >
                  <Plus className="w-4 h-4 text-red-600" />
                  <span>+ Tambah Sesi / Aktiviti Baharu</span>
                </button>
              )
            )}
          </div>

          {/* Card 3: Saluran Pembayaran Yuran Gelanggang (FPX Persatuan vs QR Sendiri Ketua Gelanggang) */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200 shadow-2xs space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <span className="p-2.5 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100">
                  <Wallet className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-sm font-black text-slate-900">Saluran Pembayaran Yuran Gelanggang</h2>
                  <p className="text-[11px] text-slate-500">Tetapkan saluran kutipan sama ada terus ke akaun rasmi persatuan (FPX) atau Kod QR sendiri.</p>
                </div>
              </div>
              <span className={`text-[10px] font-black px-2.5 py-1 rounded-full border ${paymentPreference === 'qr_ketua'
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                }`}>
                {paymentPreference === 'qr_ketua' ? 'QR Ketua Gelanggang' : 'FPX Bank Persatuan'}
              </span>
            </div>

            {/* Pilihan Saluran Pembayaran: 2 Kad Pilihan */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {/* Option 1: FPX ToyyibPay Bank Persatuan */}
              <div
                onClick={() => canEdit && setPaymentPreference('fpx')}
                className={`p-4 rounded-2xl border-2 transition-all flex flex-col justify-between space-y-3 ${canEdit ? 'cursor-pointer' : 'cursor-default opacity-90'
                  } ${paymentPreference === 'fpx'
                    ? 'bg-emerald-50/40 border-emerald-500 shadow-xs ring-2 ring-emerald-500/10'
                    : 'bg-slate-50/60 hover:bg-slate-50 border-slate-200 text-slate-700'
                  }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className={`p-2 rounded-xl ${paymentPreference === 'fpx' ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                      <CreditCard className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-xs font-black text-slate-900 block">FPX Bank Persatuan</span>
                      <span className="text-[10px] text-slate-500 block">ToyyibPay Online Gateway</span>
                    </div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${paymentPreference === 'fpx' ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300 bg-white'}`}>
                    {paymentPreference === 'fpx' && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                </div>

                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Semua yuran disalurkan terus secara automatik ke akaun rasmi <strong>Bank Muamalat Persatuan (0203-0003921-71-0)</strong> melalui FPX.
                </p>

                <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                  <span>Audit & e-Laporan</span>
                  <span className="font-bold text-emerald-700">Automatik</span>
                </div>
              </div>

              {/* Option 2: Kod QR Sendiri Ketua Gelanggang */}
              <div
                onClick={() => canEdit && setPaymentPreference('qr_ketua')}
                className={`p-4 rounded-2xl border-2 transition-all flex flex-col justify-between space-y-3 ${canEdit ? 'cursor-pointer' : 'cursor-default opacity-90'
                  } ${paymentPreference === 'qr_ketua'
                    ? 'bg-amber-50/50 border-amber-500 shadow-xs ring-2 ring-amber-500/10'
                    : 'bg-slate-50/60 hover:bg-slate-50 border-slate-200 text-slate-700'
                  }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className={`p-2 rounded-xl ${paymentPreference === 'qr_ketua' ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                      <QrCode className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-xs font-black text-slate-900 block">QR Sendiri Ketua Gelanggang</span>
                      <span className="text-[10px] text-slate-500 block">DuitNow QR / Akaun Bank</span>
                    </div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${paymentPreference === 'qr_ketua' ? 'border-amber-600 bg-amber-600' : 'border-slate-300 bg-white'}`}>
                    {paymentPreference === 'qr_ketua' && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                </div>

                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Yuran dibayar terus ke Kod QR DuitNow atau akaun bank Ketua Gelanggang. Pembayar <strong>wajib memuat naik resit bayaran (.pdf)</strong>.
                </p>

                <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                  <span>Format Resit Simpanan</span>
                  <span className="font-bold text-amber-700 font-mono text-[9px]">memberid_name_month_year.pdf</span>
                </div>
              </div>
            </div>

            {/* Konfigurasi Butiran Bank & Kod QR (Jika QR Sendiri dipilih) */}
            {paymentPreference === 'qr_ketua' && (
              <div className="space-y-4 p-4.5 bg-gradient-to-b from-amber-50/30 to-slate-50/80 rounded-2xl border border-amber-200/80 animate-fade-in">
                <div className="flex items-center gap-2 text-amber-800 text-xs font-black">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <span>Konfigurasi Kod QR & Akaun Bank Ketua Gelanggang</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Nama Bank */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 mb-1">Nama Bank *</label>
                    <select
                      value={qrBankName}
                      disabled={!canEdit}
                      onChange={(e) => setQrBankName(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-amber-500 disabled:opacity-75"
                    >
                      <option value="Maybank">Maybank (Malayan Banking)</option>
                      <option value="CIMB Bank">CIMB Bank</option>
                      <option value="Bank Islam">Bank Islam Malaysia</option>
                      <option value="RHB Bank">RHB Bank</option>
                      <option value="BSN (Bank Simpanan Nasional)">BSN (Bank Simpanan Nasional)</option>
                      <option value="Public Bank">Public Bank</option>
                      <option value="Hong Leong Bank">Hong Leong Bank</option>
                      <option value="AmBank">AmBank</option>
                      <option value="Bank Muamalat">Bank Muamalat Malaysia Berhad</option>
                      <option value="Bank Rakyat">Bank Rakyat</option>
                      <option value="Agrobank">Agrobank</option>
                      <option value="DuitNow QR Sahaja">DuitNow QR Sahaja</option>
                    </select>
                  </div>

                  {/* Nama Pemegang Akaun */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 mb-1">Nama Pemegang Akaun *</label>
                    <input
                      type="text"
                      value={qrAccountName}
                      disabled={!canEdit}
                      onChange={(e) => setQrAccountName(e.target.value)}
                      placeholder="cth: Mohd Fauzi Bin Abd Aziz"
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-amber-500 disabled:opacity-75"
                    />
                  </div>

                  {/* Nombor Akaun Bank */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 mb-1">Nombor Akaun Bank *</label>
                    <input
                      type="text"
                      value={qrAccountNo}
                      disabled={!canEdit}
                      onChange={(e) => setQrAccountNo(e.target.value.replace(/[^0-9-]/g, ''))}
                      placeholder="cth: 152012345678"
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold font-mono text-slate-900 focus:outline-none focus:border-amber-500 disabled:opacity-75"
                    />
                  </div>

                  {/* Muat Naik Fail Kod QR */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 mb-1">
                      <span>Imej Kod QR DuitNow (PNG / JPEG)</span>
                      {isQrUploading && <span className="text-amber-600 ml-1.5 font-normal">Memproses...</span>}
                    </label>
                    {canEdit ? (
                      <label className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 hover:border-amber-500 rounded-xl text-xs font-bold text-slate-700 cursor-pointer transition-colors">
                        <Upload className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="truncate">{qrImageUrl ? 'Ganti Fail Gambar QR' : 'Pilih Gambar QR DuitNow'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleQrImageUpload}
                          className="hidden"
                        />
                      </label>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-500">
                        <Upload className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="truncate">{qrImageUrl ? 'Gambar QR Telah Dimuat Naik' : 'Tiada Gambar QR'}</span>
                      </div>
                    )}
                  </div>

                  {/* Nota / Arahan Tambahan */}
                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-bold text-slate-700 mb-1">Nota / Arahan Tambahan untuk Pembayar Yuran</label>
                    <input
                      type="text"
                      value={qrNotes}
                      disabled={!canEdit}
                      onChange={(e) => setQrNotes(e.target.value)}
                      placeholder="cth: Sila jelaskan yuran sebelum 7hb setiap bulan dan lampirkan resit transaksi."
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-medium text-slate-900 focus:outline-none focus:border-amber-500 disabled:opacity-75"
                    />
                  </div>
                </div>

                {/* Pratonton Kod QR & Maklumat Bank */}
                <div className="mt-3 p-3.5 bg-white rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-center gap-4">
                  {qrImageUrl ? (
                    <div className="relative shrink-0 group">
                      <img
                        src={qrImageUrl}
                        alt="Kod QR Ketua Gelanggang"
                        className="w-28 h-28 object-contain rounded-xl border border-slate-200 shadow-3xs bg-white p-1"
                      />
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => setQrImageUrl('')}
                          className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center shadow-xs border-none cursor-pointer"
                          title="Buang Imej QR"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="w-28 h-28 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-slate-400 p-2 text-center shrink-0">
                      <QrCode className="w-8 h-8 mb-1 text-slate-300" />
                      <span className="text-[10px] font-bold leading-tight">Tiada Gambar QR Dimuat Naik</span>
                    </div>
                  )}

                  <div className="space-y-1.5 min-w-0 flex-1 text-center sm:text-left">
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                      <span className="px-2 py-0.5 bg-slate-900 text-amber-400 text-[10px] font-mono font-black rounded-md">
                        {qrBankName || 'Bank'}
                      </span>
                      {qrAccountNo && (
                        <span className="text-xs font-mono font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                          {qrAccountNo}
                        </span>
                      )}
                    </div>
                    <div className="text-xs font-extrabold text-slate-900 truncate">
                      {qrAccountName || dynamicLeader.name || 'Nama Pemegang Akaun'}
                    </div>
                    <p className="text-[11px] text-slate-500 leading-tight">
                      {qrNotes || 'Pesilat akan melihat butiran akaun ini semasa membuat bayaran yuran dan dikehendaki memuat naik resit.'}
                    </p>
                    <div className="text-[10px] text-indigo-700 font-bold flex items-center justify-center sm:justify-start gap-1">
                      <FileText className="w-3 h-3" />
                      <span>Resit disimpan ke Google Drive JSON Ahli (memberid_name_month_year.pdf)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right 1 Column: Management Details & Financial Summary */}
        <div className="space-y-6">

          {/* Card 3: Maklumat & Butiran Pengurusan Gelanggang */}
          <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-2xs space-y-4">
            <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
              <span className="p-2.5 rounded-2xl bg-blue-50 text-blue-600 border border-blue-100">
                <MapPin className="w-5 h-5" />
              </span>
              <div>
                <h3 className="text-sm font-black text-slate-900">Butiran Gelanggang & Lokasi</h3>
                <p className="text-[11px] text-slate-500">Ketua gelanggang, tempat dan pandu arah.</p>
              </div>
            </div>

            {/* Status Aktif / Tidak Aktif */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Status Gelanggang</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setStatus('Aktif')}
                  className={`py-2 px-3 rounded-xl text-xs font-extrabold border transition-all cursor-pointer ${status === 'Aktif'
                      ? 'bg-emerald-500 text-white border-emerald-500 shadow-xs'
                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                    }`}
                >
                  Aktif
                </button>
                <button
                  type="button"
                  onClick={() => setStatus('Tidak Aktif')}
                  className={`py-2 px-3 rounded-xl text-xs font-extrabold border transition-all cursor-pointer ${status === 'Tidak Aktif'
                      ? 'bg-slate-700 text-white border-slate-700 shadow-xs'
                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                    }`}
                >
                  Tidak Aktif
                </button>
              </div>
            </div>

            {/* Dynamic Ketua Gelanggang & No. Telefon Display (Non-editable, dynamically assigned from member roles) */}
            <div className="bg-slate-50/90 p-3.5 rounded-2xl border border-slate-200/80 space-y-3">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-slate-400" />
                <span>Ketua Gelanggang</span>
              </span>

              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center text-xs font-black shrink-0 shadow-2xs">
                    {dynamicLeader.name ? dynamicLeader.name.trim().charAt(0).toUpperCase() : 'K'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-black text-slate-900 block truncate" title={dynamicLeader.name}>
                      {dynamicLeader.name ? formatPublicName(dynamicLeader.name) : 'Belum Dilantik'}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono block leading-none mt-0.5">
                      {dynamicLeader.memberId ? `${dynamicLeader.memberId} • ` : ''}Ketua Gelanggang
                    </span>
                  </div>
                </div>

                {dynamicLeader.bengkung && (
                  <BengkungCulaBadge
                    bengkung={dynamicLeader.bengkung}
                    size="sm"
                    fullWidth={false}
                    className="!h-6 !text-[10px] !pl-2.5 !pr-7 rounded-lg shrink-0 shadow-3xs"
                    minWidth="min-w-[85px]"
                  />
                )}
              </div>

              {/* No. Telefon / WhatsApp */}
              <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-xs text-slate-700 font-bold min-w-0 flex-1">
                  <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="truncate font-mono text-[11px]">
                    {dynamicLeader.phone || 'Tiada No. Telefon'}
                  </span>
                </div>

                {dynamicLeader.phone && (
                  <a
                    href={`https://wa.me/${dynamicLeader.phone.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-bold flex items-center gap-1.5 transition-colors shrink-0 shadow-3xs"
                    title="Hubungi WhatsApp"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                )}
              </div>
            </div>

            {/* Nama Tempat / Lokasi */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Nama Pusat / Tempat Latihan</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="cth: Pusat Riadah Wadi AnNour"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red focus:bg-white"
              />
            </div>

            {/* Alamat Penuh */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Alamat Lengkap</label>
              <textarea
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Alamat fizikal gelanggang untuk surat & penghantaran"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-none focus:border-asmm-red focus:bg-white resize-none"
              />
            </div>

            {/* Pautan GPS / Google Maps */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Pautan GPS / Google Maps</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={gpsLocation}
                  onChange={(e) => setGpsLocation(e.target.value)}
                  placeholder="https://maps.google.com/..."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-none focus:border-asmm-red focus:bg-white"
                />
                {(gpsLocation || address || location) && (
                  <a
                    href={getGoogleMapsUrl({ gpsLocation, address, location })}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl transition-colors border border-blue-200 shrink-0"
                    title="Uji Pautan Peta"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Card 4: Ringkasan Pesilat & Unjuran Kutipan */}
          <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-2xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="p-2 rounded-xl bg-slate-900 text-amber-400 shrink-0 shadow-2xs">
                  <Users className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-xs font-black text-slate-900 tracking-tight truncate">Ringkasan Pesilat & Yuran</h3>
                  <p className="text-[10px] text-slate-400 truncate">Kutipan yuran bulanan & senarai ahli</p>
                </div>
              </div>
              <span className="text-[9px] font-extrabold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/80 shrink-0 whitespace-nowrap">
                Aktif
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <div className="bg-slate-50/90 hover:bg-slate-100/60 transition-colors p-3 rounded-2xl border border-slate-200/80 flex flex-col justify-between">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">
                  Pesilat Berdaftar
                </span>
                <div className="my-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-slate-900 leading-none">
                    {enrolledMembers.length}
                  </span>
                  <span className="text-[10px] font-bold text-slate-500">orang</span>
                </div>
                <span className="text-[9px] text-slate-400 font-medium block">
                  ahli gelanggang aktif
                </span>
              </div>

              <div className="bg-emerald-50/70 hover:bg-emerald-50 transition-colors p-3 rounded-2xl border border-emerald-200/80 flex flex-col justify-between">
                <span className="text-[9px] font-black text-emerald-800 uppercase tracking-wider block">
                  Unjuran Bulanan
                </span>
                <div className="my-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-emerald-700 leading-none">
                    RM{financialSummary.monthlyTotal}
                  </span>
                  <span className="text-[10px] font-bold text-emerald-600">/ bln</span>
                </div>
                <span className="text-[9px] text-emerald-700/80 font-medium block">
                  anggaran kutipan
                </span>
              </div>
            </div>

            <div className="p-3 bg-gradient-to-r from-emerald-50/80 via-teal-50/40 to-slate-50 border border-emerald-200/70 rounded-2xl flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-3xs">
                  <DollarSign className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-black text-slate-900">Pakej Tahunan</span>
                    <span className="text-[8px] font-black text-emerald-700 bg-emerald-100/90 px-1.5 py-0.5 rounded-sm uppercase tracking-wide leading-none">
                      -10% Jimat
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500 block truncate">
                    Jika dibayar penuh 12 bulan
                  </span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-black text-emerald-800 leading-none">
                  RM{financialSummary.yearlyTotal}
                </div>
                <div className="text-[9px] font-bold text-slate-400 leading-none mt-0.5">
                  / tahun
                </div>
              </div>
            </div>

            {/* List of enrolled members */}
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                  Senarai Pesilat Gelanggang ({enrolledMembers.length})
                </span>
                <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Aktif Latihan
                </span>
              </div>
              {enrolledMembers.length > 0 ? (
                <div className="max-h-60 overflow-y-auto space-y-1.5 pr-1 -mr-1">
                  {enrolledMembers.map(m => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between p-2 rounded-xl bg-slate-50/80 hover:bg-slate-100/90 border border-slate-200/70 transition-all text-xs gap-2 group"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="w-7 h-7 rounded-lg bg-white border border-slate-200/90 text-slate-800 flex items-center justify-center text-xs font-black shrink-0 shadow-3xs group-hover:border-slate-300">
                          {m.name?.trim()?.charAt(0)?.toUpperCase() || 'P'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="font-extrabold text-slate-900 block truncate text-[11px]" title={m.name}>
                            {formatPublicName(m.name)}
                          </span>
                          <span className="text-[9px] text-slate-400 font-mono block leading-none mt-0.5">
                            {m.id}
                          </span>
                        </div>
                      </div>
                      <BengkungCulaBadge
                        bengkung={m.bengkung || 'Putih 1'}
                        size="sm"
                        fullWidth={false}
                        className="!h-6 !text-[10px] !pl-2 !pr-7 rounded-lg shrink-0 shadow-3xs"
                        minWidth="min-w-[85px]"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-xs text-slate-400 italic bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  Tiada pesilat aktif berdaftar di gelanggang ini.
                </div>
              )}
            </div>
          </div>

          {/* Bottom Save Action */}
          {canEdit && (
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={isSaving}
              className="w-full py-3 bg-asmm-red hover:bg-asmm-crimson disabled:opacity-50 text-white text-xs font-black rounded-2xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer border-none"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Menyimpan Perubahan...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Simpan Semua Perubahan Sesi & Harga</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
